import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, ChevronDown, ChevronUp, CheckCircle2, XCircle, RotateCcw, Clock, Ban, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDay, formatTime } from '../AssignMentorDialog';
import {
  OPP_PILL, OPP_LABEL, SOURCE_LABEL, FORMAT_LABEL, CAREER_PILL, CAREER_LABEL,
  Spinner, Empty, Detail,
} from './shared';

/** The opportunity queue for one programme: submissions, approvals and proposed changes. */
export default function OffersList({ programId, focus, onChanged }) {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [status, setStatus] = useState(
    focus === 'offers_to_approve' ? 'submitted' : focus === 'offers_approved' ? 'approved' : 'all');
  const [company, setCompany] = useState('all');
  const [source, setSource] = useState('all');
  const [career, setCareer] = useState(focus === 'career_review' ? 'pending' : 'all');
  const [query, setQuery] = useState((focus || '').startsWith('q:') ? focus.slice(2) : '');
  const [review, setReview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/coordinator/internship-offers', { params: { program_id: programId } });
      setOpps(r.data || []);
    } catch { setOpps([]); }
    setLoading(false);
  }, [programId]);
  useEffect(() => { load(); }, [load]);

  const companies = useMemo(
    () => [...new Set(opps.map(o => o.company_name).filter(Boolean))].sort(), [opps]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return opps.filter(o => (status === 'all' || o.status === status)
      && (company === 'all' || o.company_name === company)
      && (source === 'all' || (o.source || 'company') === source)
      && (career === 'all' || (o.career_mapping_status || 'unmapped') === career)
      && (!q || [o.title, o.company_name, o.career_name].filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))));
  }, [opps, status, company, source, career, query]);

  const refresh = () => { load(); onChanged?.(); };
  if (loading) return <Spinner />;

  return (
    <div data-testid="offers-list">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-3.5 w-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search internship, company or career"
            className="h-9 pl-8 rounded-lg border-gray-200 text-xs bg-white" data-testid="offers-search" />
        </div>
        <FilterSelect value={company} onChange={setCompany} testId="offers-filter-company" label="All companies"
          options={companies.map(c => [c, c])} />
        <FilterSelect value={source} onChange={setSource} testId="offers-filter-source" label="All sources"
          options={Object.entries(SOURCE_LABEL)} />
        <FilterSelect value={status} onChange={setStatus} testId="offers-filter-status" label="All approval states"
          options={Object.entries(OPP_LABEL)} />
        <FilterSelect value={career} onChange={setCareer} testId="offers-filter-career" label="All career mappings"
          options={Object.entries(CAREER_LABEL)} />
      </div>

      {shown.length === 0 ? (
        <Empty testId="offers-empty" text="No opportunities match these filters. Invite a company or share your open programme link." />
      ) : (
        <div className="space-y-2">
          {shown.map(o => (
            <div key={o.id} className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden" data-testid={`opportunity-row-${o.id}`}>
              <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="w-full px-4 py-3 text-left hover:bg-[#FAFBFC] transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-[#1F2937]">{o.title}</p>
                      <Badge className={`border-0 text-[10px] ${OPP_PILL[o.status]}`} data-testid={`opp-status-${o.id}`}>{OPP_LABEL[o.status]}</Badge>
                      <Badge className="border-0 text-[10px] bg-[#F3F4F6] text-[#6B7280]">{SOURCE_LABEL[o.source] || 'Company'}</Badge>
                      {o.review_state === 'pending_reapproval' && (
                        <Badge className="border-0 text-[10px] bg-sky-50 text-sky-700" data-testid={`opp-changes-${o.id}`}>Changes to review</Badge>
                      )}
                      {o.referred_by_parent_name && (
                        <Badge className="border-0 text-[10px] bg-[#FEF0EC] text-[#F06651]" data-testid={`opp-referral-${o.id}`}>Referred by {o.referred_by_parent_name}</Badge>
                      )}
                      {o.career_mapping_status === 'pending' && (
                        <Badge className="border-0 text-[10px] bg-amber-50 text-amber-700" data-testid={`opp-career-pending-${o.id}`}>Pending career review</Badge>
                      )}
                      {o.career_mapping_status === 'rejected' && (
                        <Badge className="border-0 text-[10px] bg-red-50 text-red-600" data-testid={`opp-career-rejected-${o.id}`}>Career not approved</Badge>
                      )}
                    </div>
                    <p className="text-sm text-[#6B7280] mt-0.5">
                      {o.company_name}{o.career_name ? ` · ${o.career_name}` : ''}
                    </p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[11px] text-[#4B5563]">
                      {o.start_date && <span>{formatDay(o.start_date)} – {formatDay(o.end_date)}</span>}
                      {o.duration_days ? <span>{o.duration_days} day{o.duration_days === 1 ? '' : 's'}</span> : null}
                      {o.format && <span>{FORMAT_LABEL[o.format] || o.format}</span>}
                      {o.positions ? <span>{o.positions} position{o.positions === 1 ? '' : 's'}</span> : null}
                      {o.eligible_grades?.length ? <span>Grades {o.eligible_grades.join(', ')}</span> : null}
                      {!o.start_date && !o.positions && (
                        <span className="text-[#9CA3AF]">Details come with the company's confirmation</span>
                      )}
                    </div>
                  </div>
                  {expanded === o.id ? <ChevronUp className="h-4 w-4 text-[#9CA3AF] shrink-0" /> : <ChevronDown className="h-4 w-4 text-[#9CA3AF] shrink-0" />}
                </div>
              </button>
              {expanded === o.id && (
                <div className="px-4 pb-4 border-t border-[#F3F4F6] pt-3 space-y-3 text-xs" data-testid={`opportunity-detail-${o.id}`}>
                  <div className="flex items-center gap-2">
                    <Badge className={`border-0 text-[10px] ${CAREER_PILL[o.career_mapping_status || 'unmapped']}`}>
                      {CAREER_LABEL[o.career_mapping_status || 'unmapped']}
                    </Badge>
                    <span className="text-[#9CA3AF] text-[11px]">CareersPOV library mapping is managed by the CareersPOV admin team.</span>
                  </div>
                  <Detail label="Career (CareersPOV library)"
                    value={o.career_name
                      || (o.career_mapping_status === 'rejected'
                        ? `Requested: ${o.career_request?.name || '—'} — CareersPOV did not add this career`
                        : o.career_request?.name
                          ? `Requested: ${o.career_request.name} — pending CareersPOV review`
                          : 'Not mapped yet')} />
                  {(o.related_careers || []).length > 0 && (
                    <Detail label="Related careers" value={o.related_careers.map(c => c.name).join(' · ')} />
                  )}
                  <Detail label="Description" value={o.description} />
                  <Detail label="Student activities" value={o.student_activities} />
                  <Detail label="Learning outcomes" value={o.learning_outcomes} />
                  <Detail label="Prerequisites" value={o.prerequisites} />
                  <Detail label="Daily hours" value={o.daily_start_time ? `${formatTime(o.daily_start_time)} – ${formatTime(o.daily_end_time)}` : null} />
                  <Detail label="Workplace address" value={o.workplace_address} />
                  <Detail label="Industry / website" value={[o.industry, o.website].filter(Boolean).join(' · ')} />
                  <Detail label="Contact" value={[o.contact_name, o.contact_email, o.contact_phone].filter(Boolean).join(' · ')} />
                  <Detail label="Supervisor" value={[o.supervisor_name, o.supervisor_email, o.supervisor_phone].filter(Boolean).join(' · ')} />
                  <Detail label="Additional instructions" value={o.additional_instructions} />
                  {o.coordinator_note && <Detail label="Your note to the company" value={o.coordinator_note} />}
                  {o.reviewed_by && <Detail label="Reviewed by" value={`${o.reviewed_by}`} />}
                  {(o.company || o.source === 'partner' || o.source === 'parent_referral') && (
                    <div className="bg-[#FAFBFC] rounded-xl px-3 py-2.5 space-y-1.5" data-testid={`opp-company-${o.id}`}>
                      <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] font-semibold">Company & confirmation</p>
                      {o.company && (
                        <>
                          <Detail label="Registered name" value={o.company.name} />
                          <Detail label="Industry / website" value={[o.company.industry, o.company.website].filter(Boolean).join(' · ')} />
                          <Detail label="Business address" value={o.company.address} />
                          <Detail label="Registration number" value={o.company.registration_number} />
                          <Detail label="Authorised representative" value={[o.company.rep_name, o.company.rep_email].filter(Boolean).join(' · ')} />
                        </>
                      )}
                      <p className="text-[11px] flex items-center gap-1.5" data-testid={`opp-activation-${o.id}`}>
                        {o.partner_activated ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Clock className="h-3 w-3 text-amber-500" />}
                        <span className={o.partner_activated ? 'text-emerald-600' : 'text-amber-700'}>
                          {o.partner_activated ? 'Partner account activated' : 'Partner account not activated yet'}
                        </span>
                      </p>
                      <p className="text-[11px] flex items-center gap-1.5" data-testid={`opp-confirmed-${o.id}`}>
                        {o.company_confirmed_at ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Clock className="h-3 w-3 text-amber-500" />}
                        <span className={o.company_confirmed_at ? 'text-emerald-600' : 'text-amber-700'}>
                          {o.company_confirmed_at ? `Confirmed by ${o.company_confirmed_by || 'the representative'}` : 'Not confirmed by the company yet'}
                        </span>
                      </p>
                      <p className="text-[11px] flex items-center gap-1.5" data-testid={`opp-declaration-${o.id}`}>
                        {o.declaration ? <CheckCircle2 className="h-3 w-3 text-emerald-500" /> : <Clock className="h-3 w-3 text-amber-500" />}
                        <span className={o.declaration ? 'text-emerald-600' : 'text-amber-700'}>
                          {o.declaration ? `Declaration accepted (${o.declaration.version})` : 'Declaration not accepted yet'}
                        </span>
                      </p>
                      {o.referred_by_parent_name && <Detail label="Referred by" value={`${o.referred_by_parent_name} (parent)`} />}
                    </div>
                  )}
                  {o.review_state === 'pending_reapproval' && o.pending_changes && (
                    <div className="bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5" data-testid={`opp-pending-${o.id}`}>
                      <p className="text-[11px] font-semibold text-sky-800">
                        Changes proposed by {o.pending_changes.proposed_by} — families still see the approved version
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {Object.entries(o.pending_changes).filter(([k]) => !['proposed_at', 'proposed_by'].includes(k)).map(([k, v]) => (
                          <li key={k} className="text-[11px] text-sky-900">
                            <span className="capitalize">{k.replace(/_/g, ' ')}</span>: <strong>{Array.isArray(v) ? v.join(', ') : String(v ?? '—')}</strong>
                            <span className="text-sky-700"> (was {Array.isArray(o[k]) ? (o[k] || []).join(', ') : String(o[k] ?? '—')})</span>
                          </li>
                        ))}
                      </ul>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg text-xs" onClick={() => setReview({ opp: o, decision: 'approve_changes' })} data-testid={`approve-changes-${o.id}`}>Approve changes</Button>
                        <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setReview({ opp: o, decision: 'reject_changes' })} data-testid={`reject-changes-${o.id}`}>Keep approved version</Button>
                      </div>
                    </div>
                  )}
                  {o.career_mapping_status !== 'mapped' && o.status !== 'approved' && (
                    <p className="text-[11px] text-amber-700 flex items-start gap-1.5" data-testid={`opp-career-blocked-${o.id}`}>
                      <Ban className="h-3.5 w-3.5 shrink-0 mt-px" />
                      {o.career_mapping_status === 'rejected'
                        ? 'CareersPOV did not add the requested career. The company must link a career from the library before this internship can be published.'
                        : 'Career mapping is still with CareersPOV. You can review this submission now, but it can only be approved and published once the career is resolved.'}
                    </p>
                  )}
                  {o.can_approve === false && o.status !== 'approved' && o.career_mapping_status === 'mapped' && (
                    <p className="text-[11px] text-amber-700 flex items-start gap-1.5" data-testid={`opp-blocked-${o.id}`}>
                      <Ban className="h-3.5 w-3.5 shrink-0 mt-px" />
                      This offer can be approved once the authorised representative has activated their account,
                      confirmed the details and accepted the declaration.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {o.status !== 'approved' && (
                      <Button size="sm" disabled={o.can_approve === false} className="bg-emerald-500 text-white hover:bg-emerald-600 rounded-lg text-xs disabled:opacity-40" onClick={() => setReview({ opp: o, decision: 'approve' })} data-testid={`approve-opp-${o.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approve
                      </Button>
                    )}
                    {o.status !== 'changes_requested' && o.status !== 'rejected' && (
                      <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setReview({ opp: o, decision: 'request_changes' })} data-testid={`request-changes-opp-${o.id}`}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1" />Request Changes
                      </Button>
                    )}
                    {o.status !== 'rejected' && (
                      <Button size="sm" variant="ghost" className="rounded-lg text-xs text-red-500 hover:bg-red-50" onClick={() => setReview({ opp: o, decision: 'reject' })} data-testid={`reject-opp-${o.id}`}>
                        <XCircle className="h-3.5 w-3.5 mr-1" />Reject
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {review && <ReviewDialog {...review} onClose={() => setReview(null)} onDone={() => { setReview(null); refresh(); }} />}
    </div>
  );
}

const FilterSelect = ({ value, onChange, options, label, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className="h-9 w-[185px] rounded-lg border-gray-200 text-xs bg-white" data-testid={testId}>
      <SelectValue placeholder={label} />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="all" className="text-xs">{label}</SelectItem>
      {options.map(([v, l]) => <SelectItem key={v} value={v} className="text-xs">{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

function ReviewDialog({ opp, decision, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const titles = {
    approve: 'Approve this opportunity?',
    request_changes: 'Ask the company for changes',
    reject: 'Reject this opportunity?',
    approve_changes: 'Approve the proposed changes?',
    reject_changes: 'Keep the approved version?',
  };
  const blurbs = {
    approve: 'Only approved opportunities can be published to your students, and they stay exclusive to your school.',
    request_changes: 'The company can edit and resubmit from their Partner Dashboard. Tell them what needs changing.',
    reject: 'The company is told this opportunity will not go forward for this programme.',
    approve_changes: 'The new details replace the approved version for families. The previous version stays in the history.',
    reject_changes: 'Families keep seeing the previously approved details and the company is told why.',
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (decision === 'approve_changes' || decision === 'reject_changes') {
        await api.put(`/coordinator/internship-offers/${opp.id}/changes`, {
          decision: decision === 'approve_changes' ? 'approve' : 'reject', note: note.trim() || null });
        toast.success(decision === 'approve_changes' ? 'Changes approved' : 'Approved version kept');
      } else {
        await api.put(`/coordinator/internship-opportunities/${opp.id}/review`, { decision, note: note.trim() || null });
        toast.success(decision === 'approve' ? 'Opportunity approved' : decision === 'reject' ? 'Opportunity rejected' : 'Changes requested');
      }
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not save this decision'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">{titles[decision]}</DialogTitle>
          <DialogDescription>{opp.title} · {opp.company_name}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#6B7280] leading-relaxed">{blurbs[decision]}</p>
        <div>
          <Label className="text-sm font-medium text-[#1F2937]">
            Note to the company{decision === 'request_changes' ? ' *' : ' (optional)'}
          </Label>
          <Textarea value={note} onChange={e => setNote(e.target.value)} className="mt-1.5 rounded-xl border-gray-200 min-h-[70px] resize-none" data-testid="review-note" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={submit} disabled={saving} className={`rounded-xl font-semibold text-white ${decision === 'reject' ? 'bg-red-500 hover:bg-red-600' : 'bg-[#F97D6A] hover:bg-[#F06651]'}`} data-testid="confirm-review-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
